/**
 * frontend/src/lib/rechargerSurModuleManquant.ts
 *
 * ⚠⚠ `#1084` — **L'ONGLET RESTÉ OUVERT PENDANT UN DÉPLOIEMENT.** *Vu en production
 *    au premier login (21/09) : « **Failed to fetch dynamically imported module
 *    TwoFactorPage-….js** ». L'écran était chargé **avant** le redéploiement ; les
 *    fichiers de l'ancien build ont disparu avec lui, et la première route
 *    paresseuse demandée ensuite (`React.lazy`) a réclamé un fichier qui n'existe
 *    plus. **Rien n'est cassé côté serveur** — c'est l'onglet qui est en retard.*
 *
 * ⚠ **CE N'EST PAS UN DÉFAUT D'EN-TÊTE HTTP, ET IL FAUT LE DIRE** : `Caddyfile`
 *   envoyait **déjà** `Cache-Control: no-cache` sur l'HTML et `immutable` sur
 *   `/assets/*`. Un rechargement complet (`Ctrl+F5`) aurait suffi — mais **on ne
 *   demande pas ça à un utilisateur**, et l'écran, lui, ne disait rien d'utile.
 *   L'en-tête est durci au même lot (`no-store`), *par ceinture et bretelles* ;
 *   **c'est cette garde-ci qui répare le symptôme observé.**
 *
 * ⚠ **UNE SEULE FOIS, ET C'EST TOUT L'ENJEU** : recharger sans garde, sur une
 *   erreur qui persiste (un fichier vraiment absent, un réseau coupé), donnerait
 *   une **boucle de rechargement** — bien pire que le message d'erreur. La marque
 *   est posée **AVANT** le rechargement, dans `sessionStorage` : si la même panne
 *   revient dans la foulée, on laisse l'erreur remonter à l'`ErrorBoundary`.
 */

/** Marque de rechargement — `sessionStorage` : elle meurt avec l'onglet. */
export const CLE_RECHARGEMENT = 'orbicore_rechargement_module_manquant';

/** Au-delà de ce délai, un nouvel incident a droit à son propre rechargement. */
export const FENETRE_ANTI_BOUCLE_MS = 30_000;

/**
 * Les messages que rendent les navigateurs quand un module dynamique manque.
 * *Chrome/Edge, Firefox et Safari ne disent pas la même chose — d'où les quatre.*
 */
const MESSAGES_MODULE_MANQUANT =
  /(Failed to fetch dynamically imported module|Importing a module script failed|error loading dynamically imported module|Unable to preload CSS)/i;

/** Vrai si la raison d'un rejet ressemble à un module d'un build disparu. */
export function estModuleManquant(raison: unknown): boolean {
  if (raison == null) return false;
  const texte =
    raison instanceof Error
      ? `${raison.name}: ${raison.message}`
      : typeof raison === 'string'
        ? raison
        : String((raison as { message?: unknown }).message ?? '');
  return MESSAGES_MODULE_MANQUANT.test(texte);
}

/** Lecture tolérante de la marque — le stockage peut être refusé (navigation privée). */
function dernierRechargement(stockage: Storage | undefined): number | null {
  try {
    const brut = stockage?.getItem(CLE_RECHARGEMENT);
    if (!brut) return null;
    const valeur = Number(brut);
    return Number.isFinite(valeur) ? valeur : null;
  } catch {
    return null;
  }
}

export interface OptionsGarde {
  stockage?: Storage;
  recharger?: () => void;
  maintenant?: () => number;
}

/**
 * Décide, et agit. **Rend `true` si un rechargement a été déclenché.**
 *
 * ⚠ Sans stockage disponible, on recharge quand même **une fois** : le risque de
 *   boucle existe, mais un onglet muet sur une page blanche est pire, et le cas
 *   (stockage refusé **et** build disparu) est marginal.
 */
export function rechargerSiModuleManquant(raison: unknown, options: OptionsGarde = {}): boolean {
  if (!estModuleManquant(raison)) return false;

  const stockage = options.stockage ?? (typeof sessionStorage === 'undefined' ? undefined : sessionStorage);
  const maintenant = (options.maintenant ?? Date.now)();
  const precedent = dernierRechargement(stockage);

  if (precedent !== null && maintenant - precedent < FENETRE_ANTI_BOUCLE_MS) {
    // Déjà rechargé à l'instant : la panne n'est pas un simple retard d'onglet.
    console.warn(
      '[orbicore] module dynamique introuvable APRÈS un rechargement — on laisse l’erreur remonter (garde anti-boucle)',
    );
    return false;
  }

  // ⚠ La marque AVANT le rechargement : après, il est trop tard pour l'écrire.
  try {
    stockage?.setItem(CLE_RECHARGEMENT, String(maintenant));
  } catch {
    /* stockage refusé — on recharge quand même, une fois */
  }

  console.info('[orbicore] nouvelle version déployée — rechargement de la page');
  (options.recharger ?? (() => window.location.reload()))();
  return true;
}

/**
 * Branche la garde sur les deux chemins par lesquels l'échec arrive :
 *  · **`vite:preloadError`** — le préchargement d'un chunk a échoué. On appelle
 *    `preventDefault()` pour que Vite ne relance pas l'erreur derrière nous.
 *  · **`unhandledrejection`** — le `import()` d'un `React.lazy` qui casse sans
 *    passer par le préchargement.
 */
export function installerGardeModuleManquant(
  cible: Window = window,
  options: OptionsGarde = {},
): void {
  cible.addEventListener('vite:preloadError', (evenement) => {
    const payload = (evenement as Event & { payload?: unknown }).payload;
    if (rechargerSiModuleManquant(payload ?? evenement, options)) evenement.preventDefault();
  });

  cible.addEventListener('unhandledrejection', (evenement) => {
    const rejet = evenement as PromiseRejectionEvent;
    if (rechargerSiModuleManquant(rejet.reason, options)) rejet.preventDefault();
  });
}
